import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import {
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  IconBuilding,
  IconUserCheck,
  IconFileText,
  IconArrowLeft,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarRightCollapse,
  IconHeadset,
  IconSettings,
} from '@tabler/icons';
import { useAuth } from '../auth';
import { InternalAvatarMenu } from '../components/internal/InternalAvatarMenu';
import { LinkRouter } from '../components/header/BreadcrumbNav';
import {
  theme,
  colors,
  headerHeight,
  sidebarCollapsedWidth,
  gridContainerMinWidth,
  workframeBorderStyle,
  mainNavStyles,
  Link,
} from '../utils/theme';
import { ErrorBoundary } from 'react-error-boundary';
import { Alert } from '@mui/material';

type LayoutState = 'open' | 'closed';

const navItems = [
  { title: 'Accounts', path: '/internal/accounts', icon: IconBuilding },
  {
    title: 'Assumed Roles',
    path: '/internal/assumed-roles',
    icon: IconUserCheck,
  },
  { title: 'Audit Log', path: '/internal/audit-log', icon: IconFileText },
  { title: 'Support', path: '/internal/support', icon: IconHeadset },
  { title: 'Settings', path: '/internal/settings', icon: IconSettings },
];

const currentYear = new Date().getFullYear();

export const InternalLayout = () => {
  const [layoutState, setLayoutState] = React.useState<LayoutState>('open');
  const location = useLocation();
  const auth = useAuth();

  const layoutCols: Record<
    LayoutState,
    { header: number; breadcrumbs: number; sidenav: number; outlet: number }
  > = {
    open: { header: 2, breadcrumbs: 10, sidenav: 2, outlet: 10 },
    closed: { header: 1, breadcrumbs: 11, sidenav: 1, outlet: 11 },
  };

  const backToAppPath = auth.user?.roles[0]?.account?.name
    ? `/${auth.user.roles[0].account.name}/flows`
    : '/';

  return (
    <>
      {/* Header */}
      <Grid
        container
        sx={{
          overflow: 'hidden',
          minWidth: gridContainerMinWidth,
          height: headerHeight,
        }}
      >
        <Grid
          size={layoutCols[layoutState].header}
          style={{
            ...(layoutState === 'closed' && { width: sidebarCollapsedWidth }),
          }}
        >
          <div
            style={{
              background:
                'linear-gradient(292deg, #3F7EB1 -462.5%, #3174AB -453.36%, #F0FBFF 86.01%)',
              height: headerHeight,
              overflow: 'hidden',
            }}
          >
            <a href={backToAppPath}>
              <img
                src={`assets/images/aquifer-logo-${layoutState === 'open' ? 'rgb' : 'mark'}.svg`}
                style={{
                  display: 'block',
                  maxHeight: '32px',
                  ...(layoutState === 'closed'
                    ? { margin: '12px 0 0 20px' }
                    : { margin: '12px auto' }),
                }}
              />
            </a>
          </div>
        </Grid>

        <Grid
          size={layoutCols[layoutState].breadcrumbs}
          sx={{
            height: headerHeight,
            borderBottom: workframeBorderStyle,
            padding: 1,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 2,
            justifyContent: 'space-between',
            ...(layoutState === 'closed' && {
              width: `calc(100% - ${sidebarCollapsedWidth})`,
            }),
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                cursor: 'pointer',
                display: 'inline-block',
                paddingTop: '4px',
                paddingRight: '8px',
              }}
              onClick={() =>
                setLayoutState(layoutState === 'open' ? 'closed' : 'open')
              }
            >
              {layoutState === 'open' ? (
                <IconLayoutSidebarLeftCollapse />
              ) : (
                <IconLayoutSidebarRightCollapse />
              )}
            </span>
            <Chip
              label="Internal Admin"
              size="small"
              color="warning"
              variant="outlined"
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-end',
            }}
          >
            <InternalAvatarMenu />
          </div>
        </Grid>
      </Grid>

      {/* Sidebar + Outlet */}
      <Grid
        container
        sx={{ overflow: 'hidden', minWidth: gridContainerMinWidth }}
      >
        <Grid
          size={layoutCols[layoutState].sidenav}
          sx={{
            minHeight: `calc(100vh - ${headerHeight})`,
            background: theme.palette.common.white,
            position: 'relative',
            ...(layoutState === 'closed' && { width: sidebarCollapsedWidth }),
          }}
        >
          <List sx={{ overflow: 'hidden', paddingTop: 2 }}>
            {navItems.map(({ title, path, icon: Icon }) => {
              const isActive = location.pathname.startsWith(path);
              return (
                <ListItem
                  key={path}
                  title={title}
                  disablePadding
                  sx={{
                    display: 'block',
                    ...(isActive && mainNavStyles.navItem.active),
                  }}
                >
                  <LinkRouter
                    underline="none"
                    to={path}
                    variant="inherit"
                    color="textPrimary"
                  >
                    <ListItemButton
                      sx={[
                        { minHeight: 48, px: 2.5 },
                        layoutState === 'open'
                          ? { justifyContent: 'initial' }
                          : { justifyContent: 'center' },
                      ]}
                    >
                      <ListItemIcon
                        sx={[
                          { minWidth: 0, justifyContent: 'center' },
                          layoutState === 'open' ? { mr: 3 } : { mr: 'auto' },
                        ]}
                      >
                        <Icon
                          style={{
                            ...(isActive && { color: colors.common.black }),
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={title}
                        sx={[
                          layoutState === 'open'
                            ? {
                                ...(isActive && {
                                  color: colors.primary.dark,
                                }),
                                opacity: 1,
                              }
                            : { opacity: 0 },
                        ]}
                      />
                    </ListItemButton>
                  </LinkRouter>
                </ListItem>
              );
            })}
          </List>

          <Divider />

          <List sx={{ overflow: 'hidden' }}>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <LinkRouter
                underline="none"
                to={backToAppPath}
                variant="inherit"
                color="textPrimary"
              >
                <ListItemButton
                  sx={[
                    { minHeight: 48, px: 2.5 },
                    layoutState === 'open'
                      ? { justifyContent: 'initial' }
                      : { justifyContent: 'center' },
                  ]}
                >
                  <ListItemIcon
                    sx={[
                      { minWidth: 0, justifyContent: 'center' },
                      layoutState === 'open' ? { mr: 3 } : { mr: 'auto' },
                    ]}
                  >
                    <IconArrowLeft />
                  </ListItemIcon>
                  <ListItemText
                    primary="Back to App"
                    sx={[
                      layoutState === 'open' ? { opacity: 1 } : { opacity: 0 },
                    ]}
                  />
                </ListItemButton>
              </LinkRouter>
            </ListItem>
          </List>

          <div
            style={{
              width: '100%',
              display: 'block',
              position: 'absolute',
              bottom: 0,
              borderTop: workframeBorderStyle,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="overline"
              color="textSecondary"
              align="center"
              fontSize={10}
              style={{ lineHeight: '1.2' }}
            >
              <Link to="/privacy-policy">Privacy Policy</Link>
              <br />
              <Link to="/terms-of-service">Terms of Service</Link>
              <br />
              &copy; Aquifer, Inc. {currentYear}
            </Typography>
          </div>
        </Grid>

        <Grid
          size={layoutCols[layoutState].outlet}
          sx={{
            height: 'auto',
            padding: theme.spacing(3),
            background: theme.palette.grey[50],
            boxShadow: 'inset 5px 0 10px rgba(173, 216, 230, 0.125)',
            ...(layoutState === 'closed' && {
              width: `calc(100% - ${sidebarCollapsedWidth})`,
            }),
          }}
        >
          <ErrorBoundary
            fallback={
              <Alert
                sx={{ maxWidth: '1200px', margin: '24px auto' }}
                severity="error"
              >
                Error loading page
              </Alert>
            }
          >
            <Outlet />
          </ErrorBoundary>
        </Grid>
      </Grid>
    </>
  );
};
